import { useEffect, useRef, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export interface SignatureResult {
  type: "typed" | "drawn";
  /** base64 PNG data URL (`data:image/png;base64,...`) */
  dataUrl: string;
}

interface Props {
  signerName: string;
  onChange: (sig: SignatureResult | null) => void;
}

/**
 * Captures a signature either typed (rendered in script font then rasterized
 * to PNG) or drawn on a <canvas>. Returns base64 PNG via onChange.
 */
export function BAASignaturePad({ signerName, onChange }: Props) {
  const [mode, setMode] = useState<"typed" | "drawn">("typed");
  const [typedValue, setTypedValue] = useState(signerName);

  // Keep typed signature in sync with signer name when user hasn't overridden it.
  const userEditedRef = useRef(false);
  useEffect(() => {
    if (!userEditedRef.current) setTypedValue(signerName);
  }, [signerName]);

  // ── Typed signature → rasterize to PNG ────────────────────────────────
  useEffect(() => {
    if (mode !== "typed") return;
    if (!typedValue.trim()) {
      onChange(null);
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = 600;
    canvas.height = 140;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#0b1f44";
    ctx.font = "italic 56px 'Brush Script MT', 'Snell Roundhand', cursive";
    ctx.textBaseline = "middle";
    ctx.fillText(typedValue, 20, canvas.height / 2);
    onChange({ type: "typed", dataUrl: canvas.toDataURL("image/png") });
  }, [typedValue, mode, onChange]);

  // ── Drawn signature ───────────────────────────────────────────────────
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    if (mode !== "drawn") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0b1f44";
  }, [mode]);

  function getPos(e: React.MouseEvent | React.TouchEvent) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const point = "touches" in e ? e.touches[0] : (e as React.MouseEvent);
    return {
      x: (point.clientX - rect.left) * scaleX,
      y: (point.clientY - rect.top) * scaleY,
    };
  }

  function startDraw(e: React.MouseEvent | React.TouchEvent) {
    e.preventDefault();
    drawingRef.current = true;
    lastPosRef.current = getPos(e);
  }

  function moveDraw(e: React.MouseEvent | React.TouchEvent) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const pos = getPos(e);
    const last = lastPosRef.current ?? pos;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPosRef.current = pos;
    if (!hasDrawn) setHasDrawn(true);
  }

  function endDraw() {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPosRef.current = null;
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange({ type: "drawn", dataUrl: canvas.toDataURL("image/png") });
  }

  function clearDrawn() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onChange(null);
  }

  return (
    <Tabs value={mode} onValueChange={v => { setMode(v as "typed" | "drawn"); onChange(null); }}>
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="typed">Type</TabsTrigger>
        <TabsTrigger value="drawn">Draw</TabsTrigger>
      </TabsList>

      <TabsContent value="typed" className="space-y-3">
        <div>
          <Label htmlFor="typed-sig">Type your full legal name</Label>
          <Input
            id="typed-sig"
            value={typedValue}
            onChange={e => { userEditedRef.current = true; setTypedValue(e.target.value); }}
            placeholder="Jane M. Smith"
            className="mt-1"
            autoComplete="off"
          />
        </div>
        <div
          className="flex h-[120px] items-center rounded-lg border bg-background px-4"
          aria-label="Signature preview"
        >
          <span
            className="truncate text-4xl text-foreground"
            style={{ fontFamily: "'Brush Script MT', 'Snell Roundhand', cursive", fontStyle: "italic" }}
          >
            {typedValue || "—"}
          </span>
        </div>
      </TabsContent>

      <TabsContent value="drawn" className="space-y-3">
        <div className="rounded-lg border bg-background">
          <canvas
            ref={canvasRef}
            width={600}
            height={160}
            className="block w-full touch-none rounded-lg"
            style={{ height: 160, cursor: "crosshair" }}
            onMouseDown={startDraw}
            onMouseMove={moveDraw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={moveDraw}
            onTouchEnd={endDraw}
          />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Sign with your mouse or finger.
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={clearDrawn} disabled={!hasDrawn}>
            Clear
          </Button>
        </div>
      </TabsContent>
    </Tabs>
  );
}